import { Injectable, InjectionToken, Signal, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, from, of, race, throwError, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { ExternalLibrariesService } from 'src/app/utils/cargo-driver-check-in-service/services';
import { recaptchaEnterprise } from 'src/environments/environment';

export type RecaptchaState = 'loading' | 'ready' | 'error' | 'skipped';

export const SKIP_RECAPTCHA = new InjectionToken<Observable<boolean>>('SKIP_RECAPTCHA');

export interface RecaptchaConnectivityError {
    readonly message: string;
    readonly helperUrl: string;
}

@Injectable()
export class RecaptchaService {

    private static readonly SCRIPT_LOAD_TIMEOUT_MS = 30_000;
    private static readonly EXECUTE_TIMEOUT_MS = 10_000;

    static readonly CONNECTIVITY_ERROR: RecaptchaConnectivityError = {
        message: 'Unable to connect to reCAPTCHA. Your network may be blocking it.',
        helperUrl: '/health'
    };

    private readonly externalLibrariesService = inject(ExternalLibrariesService);
    private readonly _state = signal<RecaptchaState>('loading');
    readonly state: Signal<RecaptchaState> = this._state.asReadonly();
    readonly isReady: Signal<boolean> = computed(() => this._state() === 'ready' || this._state() === 'skipped');
    readonly isError: Signal<boolean> = computed(() => this._state() === 'error');
    readonly loadError: Signal<RecaptchaConnectivityError | null> = computed(() =>
        this._state() === 'error' ? RecaptchaService.CONNECTIVITY_ERROR : null
    );
    readonly loadError$: Observable<RecaptchaConnectivityError> = toObservable(this.loadError).pipe(
        filter((err): err is RecaptchaConnectivityError => err !== null)
    );

    constructor() {
        const skip$ = inject(SKIP_RECAPTCHA, { optional: true });

        const scriptUrl = `${recaptchaEnterprise.scriptUrl}?render=${recaptchaEnterprise.siteKey}`;

        const load$ = race(
            this.externalLibrariesService.loadScript(scriptUrl).pipe(
                switchMap(() => new Observable<RecaptchaState>(subscriber => {
                    grecaptcha.enterprise.ready(() => {
                        subscriber.next('ready');
                        subscriber.complete();
                    });
                })),
                catchError(() => of('error' as RecaptchaState))
            ),
            timer(RecaptchaService.SCRIPT_LOAD_TIMEOUT_MS).pipe(
                map(() => 'error' as RecaptchaState)
            )
        );

        const source$: Observable<RecaptchaState> = skip$
            ? skip$.pipe(
                take(1),
                switchMap(shouldSkip => shouldSkip ? of('skipped' as RecaptchaState) : load$)
            )
            : load$;

        source$.subscribe(state => this._state.set(state));
    }

    execute(action: string): Observable<string | null> {
        if (this._state() === 'skipped') {
            return of(null);
        }
        if (!this.isReady()) {
            return throwError(() => RecaptchaService.CONNECTIVITY_ERROR);
        }
        return from(
            grecaptcha.enterprise.execute(recaptchaEnterprise.siteKey, { action }) as PromiseLike<string>
        ).pipe(
            timeout({
                each: RecaptchaService.EXECUTE_TIMEOUT_MS,
                with: () => throwError(() => RecaptchaService.CONNECTIVITY_ERROR)
            })
        );
    }
}
